import React, { useMemo } from "react";
import { Target } from "lucide-react";

import { PROGRESS_DEFAULTS, TRAINING_CATEGORY_DEFAULTS } from "../../models";

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const ProgressWidget = ({ data, trainingData }) => {
  const merged = { ...PROGRESS_DEFAULTS, ...data };
  const goalCompletion = merged.goalCompletion;
  const cardio = { ...TRAINING_CATEGORY_DEFAULTS, ...merged.cardio };
  const strength = { ...TRAINING_CATEGORY_DEFAULTS, ...merged.strength };
  const flexibility = { ...TRAINING_CATEGORY_DEFAULTS, ...merged.flexibility };

  const exerciseItems = useMemo(() => {
    const bySport =
      trainingData?.bySport && typeof trainingData.bySport === "object"
        ? trainingData.bySport
        : {};

    const sports = Object.keys(bySport);
    if (sports.length === 0) return [];

    const selectedSport =
      trainingData?.lastSport && bySport[trainingData.lastSport]
        ? trainingData.lastSport
        : sports[0];

    const fields = bySport[selectedSport]?.fields || {};

    const values = Object.entries(fields)
      .map(([fieldKey, field]) => {
        const value = toNumber(field?.latest?.value);
        if (value === null) return null;
        return {
          id: fieldKey,
          label: field?.title || fieldKey,
          value,
          unit: field?.unit || "",
          sport: selectedSport,
        };
      })
      .filter(Boolean)
      .slice(0, 4);

    if (values.length === 0) return [];

    const maxValue = Math.max(...values.map((item) => item.value), 1);

    return values.map((item) => ({
      ...item,
      relativePercent: Math.max((item.value / maxValue) * 100, 8),
    }));
  }, [trainingData]);

  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset =
    circumference - (goalCompletion / 100) * circumference;

  const progressItems = [
    {
      label: "Entrenamiento de Cardio",
      progress: cardio.progress,
      sets: `${cardio.sets}/${cardio.total}`,
      type: cardio.type,
      total: cardio.total,
      color: "bg-green-500",
    },
    {
      label: "Entrenamiento de Fuerza",
      progress: strength.progress,
      sets: `${strength.sets}/${strength.total}`,
      type: strength.type,
      total: strength.total,
      color: "bg-blue-500",
    },
    {
      label: "Entrenamiento de Flexibilidad",
      progress: flexibility.progress,
      sets: `${flexibility.sets}/${flexibility.total}`,
      type: flexibility.type,
      total: flexibility.total,
      color: "bg-orange-500",
    },
  ].filter((item) => item.total > 0);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 h-full">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Target className="text-green-500" size={20} />
        <h3 className="font-semibold text-gray-900">Progreso</h3>
      </div>

      {/* Progress Circle */}
      <div className="flex flex-col items-center justify-center mb-6">
        <div className="relative mb-3">
          <svg className="w-40 h-40 transform -rotate-90">
            {/* Background circle */}
            <circle
              cx="80"
              cy="80"
              r={radius}
              stroke="#e5e7eb"
              strokeWidth="12"
              fill="transparent"
            />
            {/* Progress circle */}
            <circle
              cx="80"
              cy="80"
              r={radius}
              stroke={goalCompletion >= 100 ? "#10b981" : "#3b82f6"} // Verde si completó, Azul si no
              strokeWidth="12"
              fill="transparent"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              className="transition-all duration-1000 ease-out"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">
                {goalCompletion}%
              </div>
            </div>
          </div>
        </div>
        <div className="text-xs text-gray-600 text-center">
          Cumplimiento de la meta
        </div>
      </div>

      {/* Training Progress Items */}
      {progressItems.length > 0 && (
        <div className="space-y-4">
          {progressItems.map((item, index) => (
            <div key={index} className="space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">
                  {item.label}
                </span>
                <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  {item.sets} series
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-500 ${item.color}`}
                  style={{ width: `${item.progress}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-400 text-right italic">
                {item.type}
              </div>
            </div>
          ))}
        </div>
      )}

      {exerciseItems.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-800">
              Resultados de ejercicios
            </p>
            <span className="text-xs text-gray-500">
              {exerciseItems[0].sport}
            </span>
          </div>

          <div className="space-y-3">
            {exerciseItems.map((item) => (
              <div key={item.id} className="space-y-1">
                <div className="flex justify-between items-center gap-2">
                  <span className="text-xs font-medium text-gray-700 truncate">
                    {item.label}
                  </span>
                  <span className="text-xs font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                    {item.value}
                    {item.unit ? ` ${item.unit}` : ""}
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div
                    className="h-1.5 rounded-full bg-blue-500 transition-all duration-500"
                    style={{ width: `${item.relativePercent}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressWidget;
